// The press rule of a delegated DOM screen (ui-map-interface.md §16.8,
// R1206).
//
// A menu screen writes its controls as markup and reads every press
// through one listener on its root. This class is that listener, and it
// applies the one rule every surface keeps:
//
//   1. A control inside a host that scrolls acts on the RELEASE, through
//      the `ListTap` recogniser of `ui/touch.ts`. A finger that starts a
//      pan on a row must not press the row (R573).
//   2. A control in fixed chrome acts on the PRESS, because a control that
//      a re-render destroys between the press and the release swallows the
//      click.
//
// A field (`input`, `select`, `textarea`) keeps its native behaviour: this
// class neither acts on it nor calls `preventDefault` on it, so the caret,
// the picker and the range thumb still work.
//
// The release path calls no `preventDefault` either, the same as the core
// overlay's `releaseHost` (R578). A browser pans on `touch-action` and not
// on a cancelled `pointerdown`, but the release path has no reason to cancel
// anything, and a field beside the row keeps its native press.
//
// Enter and Space on a focused button raise one `click` with `detail` 0 and
// no pointer event. The router reads that click too, so a keyboard reaches
// every `<button>` control. A pointer click carries a count above 0 and
// already acted through the pointer path, so it is skipped here (R1569).
// A press that is no button wears `role="button"` and `tabindex="0"`, and
// the router acts on Enter and Space on it, because a key raises no click
// on such an element (R1698).
//
// `routeKeys` is that key half alone. A screen that keeps a pointer path
// of its own — the arcade match chrome, the end card of a match and the
// cover of a replay — adds it beside its pointer listener, so a key reaches
// the same handler as a pointer, and each press acts once (R1701).
//
// The caller's `act` reads the element that the press came down on. The
// recogniser keeps that element, so a release after a re-render still hands
// the old element over, and `act` must read its data attributes and not
// look it up in the document again.

import { ListTap } from './touch';

/** The elements that keep their native press. */
const FIELDS = new Set(['INPUT', 'SELECT', 'TEXTAREA', 'OPTION']);

/** True for a form field, which owns its own press. */
export function isField(el: Element): boolean {
  return FIELDS.has(el.tagName);
}

/**
 * True unless the press came from a mouse button other than the primary
 * one. A key click and a plain event carry no `button` field at all, so the
 * test asks for the field and does not lean on `undefined > 0` (R106).
 */
export function isPrimaryPress(ev: Event): boolean {
  return !('button' in ev && (ev as MouseEvent).button > 0);
}

/**
 * The control of `selector` that a press of the primary button came down
 * on, or null: the guard of a delegated listener that keeps a pointer path
 * of its own.
 */
export function pressedControl<T extends Element = HTMLElement>(ev: Event, selector: string): T | null {
  if (!isPrimaryPress(ev)) return null;
  return (ev.target as Element | null)?.closest?.<T>(selector) ?? null;
}

/** What one screen tells the router. */
export interface PressRouteOptions {
  /** The selector of a control, for example `[data-act]`. */
  control: string;
  /** True when `target` sits inside a host that scrolls under the finger. */
  scrolls: (target: HTMLElement) => boolean;
  /** Runs one control. `el` is the element that matched `control`. */
  act: (el: HTMLElement) => void;
  /**
   * True for a target that this screen must not read: a second screen
   * with a router of its own, mounted inside this root. The router then
   * ignores the press completely, so one press cannot act on two screens.
   * The arcade lobby needs it, because the hosting window and the history
   * panel stand inside its root and share its control selector (R1372).
   */
  foreign?: (target: HTMLElement) => boolean;
}

/** The element a press came down on, or null for a target that is no
 *  element or that belongs to a foreign screen. */
function ownTarget(ev: Event, opts: Pick<PressRouteOptions, 'foreign'>): HTMLElement | null {
  const target = ev.target as HTMLElement | null;
  if (!target || typeof target.closest !== 'function') return null;
  return opts.foreign?.(target) === true ? null : target;
}

/** What the key half of the router reads. */
export type KeyRouteOptions = Pick<PressRouteOptions, 'control' | 'act' | 'foreign'>;

/**
 * The two key listeners of the router on one root (R1569, R1698, R1701).
 *
 * A key press on a focused `<button>` control raises one `click` with no
 * pointer count, and `act` runs on it. A pointer click carries a count
 * above 0 and is skipped, because the pointer path acted already. Enter
 * and Space on a focused control that is no button and wears
 * `role="button"` run `act` too, because a key raises no click on such an
 * element. A button is skipped there, because the same key raises its
 * click. A held key repeats nothing, so one key press acts once and an
 * armed control (R837) takes two presses. Space would scroll the pane, so
 * its default is cancelled.
 *
 * It returns the function that removes both listeners.
 */
export function routeKeys(root: HTMLElement, opts: KeyRouteOptions): () => void {
  const onClick = (ev: MouseEvent): void => {
    if (ev.detail !== 0) return;
    const target = ownTarget(ev, opts);
    if (target === null) return;
    const el = target.closest<HTMLElement>(opts.control);
    if (el === null || el.tagName !== 'BUTTON') return;
    opts.act(el);
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    if (ev.repeat || ev.altKey || ev.ctrlKey || ev.metaKey) return;
    const target = ev.target as HTMLElement | null;
    if (!target || typeof target.matches !== 'function') return;
    if (target.tagName === 'BUTTON' || isField(target)) return;
    if (target.getAttribute('role') !== 'button' || !target.matches(opts.control)) return;
    if (opts.foreign?.(target) === true) return;
    ev.preventDefault();
    opts.act(target);
  };
  root.addEventListener('click', onClick);
  root.addEventListener('keydown', onKey);
  return () => {
    root.removeEventListener('click', onClick);
    root.removeEventListener('keydown', onKey);
  };
}

/** The four pointer listeners and the two key listeners of one screen root. */
export class PressRoute {
  private readonly tap = new ListTap<HTMLElement>();
  private readonly unkeys: () => void;

  constructor(private readonly root: HTMLElement, private readonly opts: PressRouteOptions) {
    this.tap.onTap = (el) => opts.act(el);
    root.addEventListener('pointerdown', this.onDown);
    root.addEventListener('pointermove', this.onMove);
    root.addEventListener('pointerup', this.onUp);
    root.addEventListener('pointercancel', this.onCancel);
    this.unkeys = routeKeys(root, opts);
  }

  destroy(): void {
    this.root.removeEventListener('pointerdown', this.onDown);
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerup', this.onUp);
    this.root.removeEventListener('pointercancel', this.onCancel);
    this.unkeys();
    this.tap.cancel();
  }

  private onDown = (ev: PointerEvent): void => {
    if (ev.button > 0) return;
    const target = ownTarget(ev, this.opts);
    if (target === null) return;
    const el = target.closest<HTMLElement>(this.opts.control);
    const field = isField(target) || (el !== null && isField(el));
    if (this.opts.scrolls(target)) {
      // A press on no control, or on a field, still goes to the recogniser
      // as null: it ends the press before, and a second finger stays a
      // pinch (R573).
      this.tap.down(ev.pointerId, ev.clientX, ev.clientY, field ? null : el);
      return;
    }
    if (el === null || field) return;
    ev.preventDefault();
    this.opts.act(el);
  };

  private onMove = (ev: PointerEvent): void => {
    this.tap.move(ev.pointerId, ev.clientX, ev.clientY);
  };

  private onUp = (ev: PointerEvent): void => {
    this.tap.up(ev.pointerId, ev.clientX, ev.clientY);
  };

  /** The browser took the touch for a pan of its own. */
  private onCancel = (): void => { this.tap.cancel(); };
}

/** True while `el` wears the keyboard ring. A mouse press on a button does
 *  not. An engine that cannot parse the selector answers false. */
function showsRing(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return false;
  }
}

/** One control's name across a rewrite: its tag and its `data-*`
 *  attributes, but not `data-wired`, which `wire` sets after the write. */
function controlName(el: Element): string {
  const data = [...el.attributes]
    .filter((a) => a.name.startsWith('data-') && a.name !== 'data-wired')
    .map((a) => `${a.name}=${a.value}`)
    .sort();
  return [el.tagName, ...data].join(' ');
}

/**
 * Runs `write`, which rewrites `host`, and gives the keyboard focus back to
 * the control that held it (R1698).
 *
 * A delegated screen rewrites its markup, and the rewrite destroys the
 * focused control: the focus falls to the page, and the next key press
 * reaches nothing. An armed control (R837) needs its second press on the
 * control it armed, so a keyboard player could arm it and never fire it.
 *
 * The control of the new markup with the same tag, the same `data-*`
 * attributes and the same rank among its twins takes the focus, with no
 * scroll. Only a control that wore the keyboard ring takes the focus back:
 * a mouse press leaves no ring, so a mouse player sees no change. A field
 * keeps the rule of its own screen. `ringed` replaces the ring test for a
 * test engine that has no `:focus-visible`.
 */
export function keepFocus(
  host: HTMLElement, write: () => void,
  opts: { control?: string; ringed?: (el: Element) => boolean } = {},
): void {
  const control = opts.control ?? '[data-act]';
  const was = document.activeElement;
  let name = '';
  let rank = -1;
  if (was instanceof HTMLElement && was !== host && host.contains(was) && !isField(was)
    && was.matches(control) && (opts.ringed ?? showsRing)(was)) {
    name = controlName(was);
    rank = [...host.querySelectorAll(control)].filter((el) => controlName(el) === name).indexOf(was);
  }
  write();
  if (rank < 0) return;
  const back = [...host.querySelectorAll<HTMLElement>(control)]
    .filter((el) => controlName(el) === name)[rank];
  if (back !== undefined && back !== document.activeElement) back.focus({ preventScroll: true });
}
